import React, { useEffect, useRef, useState } from 'react';
import { getMetaAccessRequest, submitMetaAccessRequest } from '../api/adpilotApi';

export default function MetaAccessRequest({ user, authorizationUrl }) {
  const [profileLink, setProfileLink] = useState('');
  const [request, setRequest] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submitting = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await getMetaAccessRequest();
        if (!cancelled) {
          setRequest(saved);
          setProfileLink(saved?.profileLink || (saved?.profileId ? `https://www.facebook.com/profile.php?id=${saved.profileId}` : ''));
        }
      } catch {
        if (!cancelled) setError('Could not check an earlier request. You can still submit your profile link.');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const alreadySent = request?.profileLink === profileLink.trim();
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || alreadySent) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const saved = await submitMetaAccessRequest(profileLink.trim());
      setRequest(saved);
      setProfileLink(saved.profileLink);
      setEditing(false);
    } catch (sendError) {
      setError(sendError?.response?.data?.error || 'We could not send your request. Please try again.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const connectLink = authorizationUrl
    ? <a className="btn btn-primary" href={authorizationUrl}>Connect Meta</a>
    : <button type="button" className="btn btn-primary" disabled>Preparing connection…</button>;

  return (
    <div className="meta-access-request">
      <ol className="meta-access-steps" aria-label="Meta connection steps">
        <li aria-current={!request ? 'step' : undefined}><span>1</span> Request access</li>
        <li aria-current={request ? 'step' : undefined}><span>2</span> Accept invitation</li>
        <li><span>3</span> Connect Meta</li>
      </ol>
      {loading ? <p className="card-copy" role="status">Loading your request…</p> : (
        <>
          <h2 className="card-title">{request && !editing ? 'Accept your invitation on Meta' : 'Let’s connect your Facebook account'}</h2>
          {!request && <p className="card-copy">Share your personal Facebook profile link. Our team will arrange app access, then you can accept the invitation on Meta for Developers.</p>}
          {request && !editing && (
            <>
              <div className="notice meta-access-confirmation" role="status">
                <strong>Access request sent</strong>
                <p>Thanks! Our team will invite the Facebook profile you shared. Once the invitation is ready, you can accept it directly on Meta for Developers.</p>
              </div>
              <p className="card-copy">Open Meta for Developers and sign in with the same Facebook profile you shared. Go to <strong>Requests</strong> and accept our app invitation. You don’t need to wait for an email from Meta.</p>
              <a className="btn btn-secondary" href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">Open Meta for Developers</a>
              <p className="card-copy">If you don’t see the request yet, our team may still be setting up your access. Please check back shortly and make sure you’re signed in with the correct Facebook profile.</p>
              <p className="meta-access-profile"><span>Facebook profile</span><a href={profileLink} target="_blank" rel="noopener noreferrer">{profileLink}</a></p>
              <button type="button" className="summary-action" onClick={() => { setEditing(true); setError(null); }}>Edit profile link</button>
              <div className="meta-access-next">
                <h3>Accepted your invitation?</h3>
                <p className="card-copy">Come back here and connect Meta, then choose your ad account and the campaigns to monitor.</p>
                {connectLink}
              </div>
            </>
          )}
          {(!request || editing) && (
            <form onSubmit={submit} className="meta-access-form">
              <label className="builder-field">
                <span id="meta-profile-link-label">Your Facebook profile link</span>
                <input type="text" inputMode="url" autoComplete="url" maxLength={2048} value={profileLink}
                  placeholder="https://www.facebook.com/your.username"
                  disabled={busy} required aria-labelledby="meta-profile-link-label" aria-describedby="meta-profile-link-help"
                  onChange={(event) => setProfileLink(event.target.value)} />
                <small id="meta-profile-link-help">Example: https://www.facebook.com/your.username. Use your own personal profile, not a business Page.</small>
              </label>
              <details className="card-copy meta-access-help">
                <summary>Where do I find my profile link?</summary>
                <p>On Facebook, open your personal profile. In a browser, copy the address from the address bar. In the Facebook app, tap the three dots on your profile and choose Copy link.</p>
              </details>
              {error && <p className="notice notice-error" role="alert">{error}</p>}
              <div className="campaign-actions">
                <button type="submit" className="btn btn-primary" disabled={busy || alreadySent || !profileLink.trim()}>
                  {busy ? 'Sending request…' : request ? 'Send updated link' : 'Request access'}
                </button>
                {editing && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setEditing(false); setProfileLink(request.profileLink); setError(null); }}>Cancel</button>}
              </div>
              <p className="card-copy">We’ll send your link and contact details to our team. After requesting access, follow the steps here to accept your invitation on Meta.</p>
            </form>
          )}
          {!request && <details className="card-copy meta-access-help"><summary>Already have app access?</summary><p>If our team has already approved you and you’ve accepted Meta’s invitation, you can connect now.</p>{connectLink}</details>}
        </>
      )}
    </div>
  );
}
